import Label from '@/components/elements/Label/Label';
import Icon from '@/components/icons/Icon';
import { ReadMore } from '@/components/modules/readmore';
import { classnames } from '@/helpers/general';
import { isEmpty } from '@/helpers/objects';
import useMobileView from '@/hooks/useMobileView';
import { _s, _t } from '@/locale';
import Card from '../Card';
import { MIDDLE_DOT_STYLE } from './ServiceListCard.constants';
import {
  getFormattedDiscountedPrice,
  getFormattedPrice,
  getPriceLabel,
  getValidPriceTypeForDiscount,
} from './ServiceListCard.helper';
import { ServiceCardProps } from './ServiceListCard.types';

const LegacyServiceCard = ({
  name,
  highlightedName,
  id,
  bundle,
  employeeName,
  description,
  duration,
  price,
  discount = {},
  onShowReadMore,
  firstAvailableTime,
  onRowClick,
  rightSlot,
  alignPriceColumn,
  hidePriceLabel,
  leftSlot,
  tags = {},
  readMore,
  timeslot = {},
}: ServiceCardProps) => {
  const validPriceTypeForDiscount = getValidPriceTypeForDiscount(price, discount.amounts);
  const hasLabels = tags.wellnessCard || discount.percentage || tags.waitingList || !isEmpty(timeslot);
  const hasDiscounts = Boolean(discount.amounts?.length) || (Boolean(discount.validity) && price?.type === 'variable');
  const { isMobileView } = useMobileView();

  const originalFormattedPrice = price
    ? getFormattedPrice({
        type: price?.type,
        value: price?.value,
        isMobileView: isMobileView,
        hasDiscounts: hasDiscounts && validPriceTypeForDiscount,
        hidePriceLabel,
      })
    : undefined;

  const discountedFormattedPrice = getFormattedDiscountedPrice(
    hasDiscounts,
    price,
    discount.amounts ?? [],
    isMobileView,
  );

  const handleShowReadMore = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onShowReadMore(name, description, id);
  };

  return (
    <>
      <Card size="sm">
        <div
          onClick={onRowClick}
          role={onRowClick ? 'button' : ''}
          className={`gap-sm flex ${isMobileView ? 'flex-col' : ''}`}>
          <div className={`flex min-w-0 flex-1 items-center justify-between`}>
            <div className={`gap-sm flex flex-1 overflow-hidden ${hasLabels ? 'items-start' : 'items-center'}`}>
              {!isMobileView && leftSlot}
              <div className="gap-xs flex min-w-0 flex-1 flex-col">
                <div className="flex justify-between">
                  <div className="gap-sm flex min-w-0 flex-1">
                    {isMobileView && leftSlot}
                    <div
                      className={`gap-2xs flex min-w-0 flex-1 flex-col overflow-hidden ${
                        isMobileView ? 'justify-center' : ''
                      }`}>
                      <h2 className="text-l text-fg_primary">{highlightedName ?? name}</h2>
                      {employeeName && <span className="text-m text-fg_secondary">{employeeName}</span>}
                      <div className="text-s gap-2xs flex items-center">
                        {(duration || bundle || discount.validity) && (
                          <div className={`flex items-center ${readMore ? 'w-full' : ''}`}>
                            {!isMobileView && duration && (
                              <span className="text-fg_secondary whitespace-nowrap font-bold">{duration}</span>
                            )}
                            {bundle?.quantity && !isMobileView && (
                              <span
                                className={`text-s text-fg_secondary ml-2xs my-1 font-bold ${
                                  duration ? `${MIDDLE_DOT_STYLE} flex before:mt-[5px]` : ''
                                }`}>{`${_s('buyBundles.quantity', {
                                count: bundle.quantity,
                              })}`}</span>
                            )}
                            {(bundle?.validity || discount.validity) && (
                              <span
                                className={`text-fg_secondary text-s whitespace-nowrap ${
                                  isMobileView ? '' : 'ml-2xs my-1'
                                } ${
                                  (bundle?.quantity || duration) && !isMobileView
                                    ? `${MIDDLE_DOT_STYLE} flex before:mt-[5px]`
                                    : ''
                                }`}>
                                {bundle?.validity ?? discount?.validity}
                              </span>
                            )}
                          </div>
                        )}
                        {!isMobileView && readMore}
                      </div>
                      {description && !isMobileView && !readMore && (
                        <ReadMore
                          text={description}
                          maxLines={1}
                          className={`text-fg_secondary text-s flex items-center`}
                          onShowReadMore={onShowReadMore ? () => onShowReadMore(name, description, id) : undefined}
                        />
                      )}
                      {firstAvailableTime && <span className="text-m text-fg_secondary">{firstAvailableTime}</span>}
                    </div>
                    {readMore && isMobileView && <div className="flex flex-shrink-0 items-center">{readMore}</div>}
                  </div>

                  {description && isMobileView && onShowReadMore && (
                    <div className={`text-s text-fg_link flex flex-shrink-0 ${isMobileView ? '' : 'items-end'}`}>
                      <button className="outline-none" onClick={handleShowReadMore}>
                        {isMobileView ? (
                          <Icon variant="info-circle" style={{ width: '20px', height: '20px' }} color="fg_link" />
                        ) : (
                          _t('learnMore')
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {hasLabels && (
                  <div className="gap-sm flex flex-wrap">
                    {timeslot.fullyBooked && <Label variant="fully-booked" size="lg" />}
                    {timeslot && !timeslot.fullyBooked && timeslot.capacity && timeslot.capacity <= 3 && (
                      <Label
                        variant="spotsLeft"
                        size="lg"
                        label={_t(`${timeslot.capacity === 1 ? 'availableCapacity_one' : 'availableCapacity_other'}`, {
                          count: timeslot.capacity,
                        })}
                      />
                    )}
                    {timeslot && !timeslot.fullyBooked && timeslot.capacity > 3 && (
                      <Label variant="available" size="lg" />
                    )}
                    {timeslot.from && <Label variant="time" size="lg" label={timeslot.from} />}
                    {tags.waitingList && <Label variant="fully-booked" label={_t('fullBookedWaitlist')} />}
                    {tags.wellnessCard && (
                      <Label size="lg" variant="wellness-card" icon={false} label={_t('wellness')} />
                    )}
                    {discount.percentage && (
                      <Label variant="smt" label={`-${discount.percentage}%`} icon={{ variant: 'smt' }} />
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div
            className={classnames('border-border_regular', {
              'border-t': isMobileView && ((price && (price.type !== 'variable' || hasDiscounts)) || duration),
              'border-l': !isMobileView && price && (price.type !== 'variable' || hasDiscounts),
            })}>
            <div
              className={classnames(
                'text-s gap-sm flex h-full items-center',
                isMobileView ? 'pt-sm justify-between' : 'pl-sm py-xs',
                !isMobileView && alignPriceColumn ? 'min-w-[164px] justify-end' : '',
              )}>
              <div className="gap-2xs flex flex-col justify-center">
                {price && (price.type !== 'variable' || hasDiscounts) && (
                  <div
                    className={`flex ${
                      isMobileView ? 'gap-xs items-center' : 'gap-2xs flex-col items-end justify-center'
                    }`}>
                    {!isMobileView && !hidePriceLabel && (!hasDiscounts || price.type === 'variable') && (
                      <span className="text-s text-fg_secondary w-full text-right">
                        {getPriceLabel(price?.type, hasDiscounts)}
                      </span>
                    )}
                    {hasDiscounts && isMobileView && discountedFormattedPrice && (
                      <span className="text-m text-fg_highlight font-bold">{discountedFormattedPrice}</span>
                    )}
                    {originalFormattedPrice && (
                      <span
                        className={classnames(
                          hasDiscounts && discountedFormattedPrice && 'text-fg_secondary',
                          hasDiscounts && discountedFormattedPrice && 'line-through',
                          (!hasDiscounts || !discountedFormattedPrice) && 'text-m font-bold',
                        )}>
                        {originalFormattedPrice}
                      </span>
                    )}
                    {hasDiscounts && !isMobileView && discountedFormattedPrice && (
                      <span className="text-m text-fg_highlight font-bold">{discountedFormattedPrice}</span>
                    )}
                  </div>
                )}
                {isMobileView && (duration || bundle?.quantity) && (
                  <div className="flex">
                    {duration && <span className="text-fg_secondary whitespace-nowrap font-bold">{duration}</span>}
                    {bundle?.quantity && (
                      <span
                        className={`text-s text-fg_secondary ml-2xs flex font-bold ${
                          duration ? `${MIDDLE_DOT_STYLE} before:mt-[5px]` : ''
                        }`}>{`${_s('buyBundles.quantity', {
                        count: bundle.quantity,
                      })}`}</span>
                    )}
                  </div>
                )}
              </div>

              {rightSlot && rightSlot}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
};

export default LegacyServiceCard;
